<template>
  <studio-tool-shell
    title="Studio Admin"
    description="Standorte, Kontaktdaten, Integrationen und betriebliche Einstellungen zentral verwalten."
    icon="mdi-shield-home-outline">
    <template v-slot:actions>
      <v-btn color="#009640" depressed class="primary-action" @click="addStudio">
        <v-icon left>mdi-plus</v-icon>
        Studio hinzufügen
      </v-btn>
    </template>

    <section class="studio-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Standorte</div>
          <h2>Studio-Konfigurationen</h2>
          <p>Öffne einen Standort, um seine Stammdaten und Schnittstellen zu bearbeiten.</p>
        </div>
        <span class="count-pill">{{ studios.length }} Studios</span>
      </div>

      <div v-if="isLoading" class="surface-state">
        <v-progress-circular indeterminate color="#58d388" size="42"></v-progress-circular>
        <p>Studios werden geladen...</p>
      </div>

      <div v-else-if="!studios.length" class="surface-state">
        <span class="state-mark"><v-icon>mdi-home-city-outline</v-icon></span>
        <h3>Noch keine Studios vorhanden</h3>
        <p>Lege den ersten Standort direkt über den Button oben an.</p>
      </div>

      <v-expansion-panels v-else v-model="panel" multiple dark flat class="studio-panels">
        <v-expansion-panel v-for="(studio, index) in studios" :key="studio._id || `new-studio-${index}`" class="studio-panel">
          <v-expansion-panel-header class="studio-panel-header">
            <div class="studio-summary">
              <span class="studio-mark"><v-icon>mdi-office-building-marker-outline</v-icon></span>
              <div class="studio-summary-copy">
                <strong>{{ studio.name || "Neues Studio" }}</strong>
                <span>{{ studio.code ? studio.code.toUpperCase() : "Noch ohne Studiocode" }}</span>
              </div>
              <span class="status-pill" :class="{ muted: studio.hiddenStudio }">
                {{ studio.hiddenStudio ? "Versteckt" : "Sichtbar" }}
              </span>
            </div>
          </v-expansion-panel-header>

          <v-expansion-panel-content class="studio-panel-content">
            <section class="form-section">
              <div class="section-heading">
                <v-icon>mdi-domain</v-icon>
                <div>
                  <h3>Stammdaten</h3>
                  <p>Name, Code und Unternehmenszuordnung</p>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12" md="6"><v-text-field v-model="studio.name" label="Name" outlined dense dark hide-details="auto" /></v-col>
                <v-col cols="12" sm="6" md="3"><v-text-field v-model="studio.code" label="Code" outlined dense dark hide-details="auto" /></v-col>
                <v-col cols="12" sm="6" md="3"><v-text-field v-model="studio.canton" label="Kanton" outlined dense dark hide-details="auto" /></v-col>
                <v-col cols="12" md="6"><v-text-field v-model="studio.art" label="Firma" outlined dense dark hide-details="auto" /></v-col>
                <v-col cols="12" md="6"><v-text-field v-model="studio.company" label="Company" outlined dense dark hide-details="auto" /></v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="section-heading">
                <v-icon>mdi-card-account-phone-outline</v-icon>
                <div>
                  <h3>Kontakt und Adresse</h3>
                  <p>Erreichbarkeit und Standortinformationen</p>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12" md="8"><v-text-field v-model="studio.adresse" label="Adresse" outlined dense dark hide-details="auto" /></v-col>
                <v-col cols="12" md="4"><v-text-field v-model="studio.telefon" label="Telefon" outlined dense dark hide-details="auto" /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.email" label="E-Mail" type="email" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"><v-text-field v-model="studio.maps" label="Google Maps URL" outlined dense dark hide-details="auto" /></v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="section-heading">
                <v-icon>mdi-web</v-icon>
                <div>
                  <h3>Web und Social Media</h3>
                  <p>Öffentliche Links und Bildressourcen</p>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.website" label="Webseite URL" type="url" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.websitePath" label="Webseite Unterseite" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12"><v-text-field v-model="studio.logo" label="Logo" outlined dense dark hide-details="auto" /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.instagram" label="Instagram URL" type="url" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.facebook" label="Facebook URL" type="url" outlined dense dark hide-details="auto"
                /></v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="section-heading">
                <v-icon>mdi-link-variant</v-icon>
                <div>
                  <h3>Betrieb und Schnittstellen</h3>
                  <p>Krankenkasse, Courts Online und TAC</p>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.insuranceCompany" label="Krankenkassen-Unternehmen" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.healtcareConfirmationNumber" label="Center ID / ZSR Nummer" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.courtsOnlineLink" label="Courts Online Link" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.courtsOnlineText" label="Courts Online Text" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" sm="4"
                  ><v-text-field v-model="studio.qm" label="Mietfläche" type="number" suffix="m²" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" sm="4"
                  ><v-text-field v-model="studio.tacPropertyId" label="TAC Property ID" type="number" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" sm="4"
                  ><v-text-field v-model="studio.tacResortId" label="TAC Resort ID" type="number" outlined dense dark hide-details="auto"
                /></v-col>
              </v-row>
            </section>

            <section class="form-section">
              <div class="section-heading">
                <v-icon>mdi-map-marker-radius-outline</v-icon>
                <div>
                  <h3>Position und Sichtbarkeit</h3>
                  <p>Koordinaten und lokale Optionen</p>
                </div>
              </div>
              <v-row dense>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.location.lat" label="Latitude" type="number" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-text-field v-model="studio.location.long" label="Longitude" type="number" outlined dense dark hide-details="auto"
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-switch v-model="studio.parkingAvailable" color="#009640" inset label="Parkmöglichkeiten vorhanden" hide-details
                /></v-col>
                <v-col cols="12" md="6"
                  ><v-switch v-model="studio.hiddenStudio" color="#009640" inset label="Studio in Auswahlen verstecken" hide-details
                /></v-col>
              </v-row>
            </section>

            <div class="panel-actions">
              <v-btn color="#009640" depressed class="primary-action" :loading="isStudioSaving(studio)" @click="saveStudio(studio)">
                <v-icon left>mdi-content-save-outline</v-icon>
                Studio speichern
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>
  </studio-tool-shell>
</template>

<script>
import api from "../../api"
import StudioToolShell from "./studio/components/StudioToolShell.vue"

export default {
  name: "studioEditor",
  components: { StudioToolShell },
  data() {
    return {
      studios: [],
      panel: [],
      isLoading: true,
      savingStudios: [],
    }
  },
  created() {
    this.fetchStudios()
  },
  methods: {
    createEmptyStudio() {
      return {
        name: "Neues Studio",
        canton: "",
        code: "",
        adresse: "",
        maps: "",
        telefon: "",
        email: "",
        website: "",
        websitePath: "",
        art: "",
        company: "",
        insuranceCompany: "",
        courtsOnlineLink: "",
        courtsOnlineText: "",
        logo: "",
        instagram: "",
        facebook: "",
        qm: 0,
        location: { lat: 0, long: 0 },
        healtcareConfirmationNumber: "",
        tacPropertyId: "",
        tacResortId: "",
        parkingAvailable: false,
        hiddenStudio: false,
      }
    },
    addStudio() {
      this.studios.unshift(this.createEmptyStudio())
      this.panel = [0]
    },
    isStudioSaving(studio) {
      return this.savingStudios.includes(studio)
    },
    async saveStudio(studio) {
      this.savingStudios.push(studio)
      try {
        const savedStudio = await api.updateStudio(studio)
        if (savedStudio && typeof savedStudio === "object") Object.assign(studio, savedStudio)
        this.$toast.success(`${studio.name || "Studio"} wurde gespeichert`)
      } catch (error) {
        console.error("Fehler beim Speichern des Studios:", error)
        this.$toast.error("Studio konnte nicht gespeichert werden")
      } finally {
        this.savingStudios = this.savingStudios.filter((item) => item !== studio)
      }
    },
    async fetchStudios() {
      this.isLoading = true
      try {
        const studios = await api.fetchStudios()
        this.studios = studios
          .filter((studio) => studio.code !== "ot")
          .map((studio) => ({
            ...studio,
            location: studio.location || { lat: 0, long: 0 },
            healtcareConfirmationNumber: studio.healtcareConfirmationNumber || "",
            tacResortId: studio.tacResortId || "",
            tacPropertyId: studio.tacPropertyId || "",
          }))
      } catch (error) {
        console.error("Fehler beim Laden der Studios:", error)
        this.$toast.error("Studios konnten nicht geladen werden")
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style scoped>
.studio-surface {
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background: var(--surface);
}

.surface-header,
.studio-summary,
.section-heading,
.panel-actions {
  display: flex;
  align-items: center;
}

.surface-header {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.125rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
}

.surface-header p,
.section-heading p,
.surface-state p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.count-pill,
.status-pill {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 750;
}

.count-pill {
  padding: 0.4375rem 0.75rem;
  color: #d9f8e5;
  background: rgba(0, 150, 64, 0.14);
}

.surface-state {
  display: grid;
  min-height: 16rem;
  place-items: center;
  align-content: center;
  text-align: center;
}

.surface-state h3 {
  margin: 0.875rem 0 0;
  font-size: 1.125rem;
}

.state-mark,
.studio-mark {
  display: grid;
  place-items: center;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.state-mark {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
}

.studio-panels {
  gap: 0.75rem;
}

.studio-panel {
  overflow: hidden;
  border: 1px solid var(--border) !important;
  border-radius: 1.125rem !important;
  background: rgba(20, 20, 21, 0.76) !important;
}

.studio-panel::before {
  box-shadow: none !important;
}

.studio-panel-header {
  min-height: 5rem;
  padding: 0.875rem 1rem;
}

.studio-summary {
  min-width: 0;
  width: 100%;
  gap: 0.75rem;
}

.studio-mark {
  flex: 0 0 2.75rem;
  width: 2.75rem;
  height: 2.75rem;
  border: 1px solid rgba(0, 150, 64, 0.24);
  border-radius: 0.8125rem;
}

.studio-summary-copy {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.studio-summary-copy strong {
  overflow: hidden;
  color: var(--text);
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.studio-summary-copy span {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
  letter-spacing: 0.05em;
}

.status-pill {
  margin-right: 0.625rem;
  padding: 0.375rem 0.625rem;
  color: #bff3d1;
  background: rgba(0, 150, 64, 0.13);
}

.status-pill.muted {
  color: #d3d3d5;
  background: rgba(255, 255, 255, 0.07);
}

.studio-panel-content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 1rem 1rem;
}

.form-section {
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.form-section + .form-section {
  margin-top: 0.75rem;
}

.section-heading {
  align-items: flex-start;
  gap: 0.625rem;
  margin-bottom: 0.875rem;
}

.section-heading > .v-icon {
  color: #58d388;
}

.section-heading h3 {
  margin: 0;
  color: var(--text);
  font-size: 1rem;
}

.panel-actions {
  justify-content: flex-end;
  padding-top: 1rem;
}

.primary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.13);
}

@media (hover: hover) and (pointer: fine) {
  .studio-panel:hover {
    border-color: var(--border-strong) !important;
  }
}

@media (max-width: 37.4375rem) {
  .studio-surface {
    padding: 0.875rem;
    border-radius: 1.125rem;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .studio-panel-header {
    padding: 0.75rem;
  }

  .status-pill {
    display: none;
  }

  .studio-panel-content ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 0.75rem 0.75rem;
  }

  .form-section {
    padding: 0.75rem;
  }

  .panel-actions .v-btn {
    width: 100%;
  }
}
</style>
